import React from "react";
import { TableCell, TableRow } from "@shadcn-in-nx/ui/table";
import { Checkbox } from "@shadcn-in-nx/ui/checkbox";
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuTrigger,
} from "@shadcn-in-nx/ui/context-menu";
import { Trash2 } from "lucide-react";
import TableCellComponent from "./TableCell";
import { ChangeType } from "../hooks/useStagedChanges";
import { inferFieldComponent } from "../utils/inferInputFieldComponent";

const ROW_HEIGHT = 36;
const EXPANDED_ROW_HEIGHT = 72;
const EXPANDED_TYPES = new Set(["textarea", "json-dialog"]);

const changeTypeStyles = {
    [ChangeType.INSERT]: "bg-green-50 hover:bg-green-100",
    [ChangeType.UPDATE]: "bg-amber-50 hover:bg-amber-100",
    [ChangeType.DELETE]: "bg-red-50 hover:bg-red-100 line-through opacity-60",
    [ChangeType.NONE]: "hover:bg-muted/50",
};

const TableRowComponent = React.memo(
    ({
        record,
        columns,
        editingCell,
        newRecord,
        selectedRecords,
        onSelectRecord,
        handleCellClick,
        handleClearEditingCell,
        handleSaveCell,
        savingRecordId,
        changeType = ChangeType.NONE,
        onOpenModal,
        onDeleteRow,
    }) => {
        const isEditing = editingCell?.recordId === record.id;
        const editingColumn = isEditing
            ? columns.find((c) => c.name === editingCell.columnName)
            : null;
        const rowHeight =
            isEditing && editingColumn && EXPANDED_TYPES.has(inferFieldComponent(editingColumn))
                ? EXPANDED_ROW_HEIGHT
                : ROW_HEIGHT;

        const handleDeleteClick = () => {
            onDeleteRow?.(record.id);
        };

        return (
            <ContextMenu>
                <ContextMenuTrigger asChild>
                    <TableRow
                        className={`${changeTypeStyles[changeType] || changeTypeStyles[ChangeType.NONE]} border-b border-border group`}
                        style={{ height: rowHeight }}
                    >
                        <TableCell
                            className="w-10 px-2"
                            style={{ height: rowHeight }}
                        >
                            <Checkbox
                                checked={selectedRecords.has(record.id)}
                                onCheckedChange={() => onSelectRecord(record.id)}
                                className={selectedRecords.has(record.id) ? "opacity-100" : "opacity-0 group-hover:opacity-100"}
                            />
                        </TableCell>
                        {columns.map((column) => (
                            <TableCell
                                key={`${record.id}-${column.name}`}
                                className="p-0 overflow-hidden"
                                style={{ height: rowHeight, maxWidth: 300 }}
                            >
                                <TableCellComponent
                                    record={record}
                                    column={column}
                                    isEditing={
                                        editingCell?.recordId === record.id &&
                                        editingCell?.columnName === column.name
                                    }
                                    isNew={record.id === newRecord}
                                    value={record[column.name]}
                                    onCellClick={handleCellClick}
                                    onClearEditingCell={handleClearEditingCell}
                                    onSave={handleSaveCell}
                                    savingRecordId={savingRecordId}
                                    onOpenModal={onOpenModal}
                                />
                            </TableCell>
                        ))}
                        <TableCell
                            className="w-20"
                            style={{ height: rowHeight }}
                        />
                    </TableRow>
                </ContextMenuTrigger>
                <ContextMenuContent>
                    <ContextMenuItem
                        onClick={handleDeleteClick}
                        className="text-red-600 focus:text-red-600"
                    >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete row
                    </ContextMenuItem>
                </ContextMenuContent>
            </ContextMenu>
        );
    }
);

TableRowComponent.displayName = "TableRowComponent";
export default TableRowComponent;
